"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Loader2, Pencil } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAgentId } from "@/context/AgentIdContext"; // Import useAgentId
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, isValid } from "date-fns";
import ChipNumberInput from "@/components/pet-details/ChipNumberInput";
import {
  clampDeductibleResetDate,
  getDefaultDeductibleResetDate,
} from "@/components/price-customization/DeductibleResetDateInput";
import { BaseCoverageId } from "@/config/coverages";
import { PREEXISTING_CONDITION_CATEGORIES } from "@/config/preexistingConditionCategories";
import { OfferResponse } from "@/types/simpego-api";
import OfferCoverageList from "@/components/price-customization/OfferCoverageList";

interface ConfirmationPageProps {
  finalPrice: number;
  /** The offer the price was calculated for; its coverages are listed like in the comparison table. */
  currentOffer: OfferResponse | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string;
  contactPhone: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  zipCode: string;
  country: string;
  petType: "cat" | "dog";
  petName: string;
  breed: string | null;
  breedLabel: string | null;
  birthDate: Date;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown";
  isCastrated: boolean;
  isHouseCat: boolean;
  chipNumber: string | null;
  preexistingConditionCategories: string[];
  preexistingConditionComment: string | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  declinedCoverages?: Partial<Record<BaseCoverageId, string>>;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  annualDeductible: number | null;
  /** Shown on the summary when the customer entered one. */
  campaignCode?: string;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  dailyTermination: boolean;
  dispatchContract: "customer" | "customer_and_agent" | "agent";
  dispatchInvoice: "customer" | "customer_and_agent" | "agent";
  dispatchReminder: "customer" | "customer_and_agent" | "agent";
  dispatchEmail: "customer" | "customer_and_agent" | "agent";
  agentDispatchEmail?: string | null;
  /** The Beratungsprotokoll attached to the offer (agents only); shown read-only, changed on the personal-details page. */
  consultationProtocolDocumentId: number | null;
  consultationProtocolFilename: string | null;
  onStartNewQuote: () => void;
  onBack: () => void;
  onApplyOffer: (firstName: string, lastName: string) => void;
  isLoading: boolean;
  onEditPlanAndCoverage: () => void;
  /** The pencil next to the package name: change the package (product variants) when the customer
   * took the "Auswählen" fast path, otherwise reopen the customization page. */
  onEditPetDetails: () => void;
  onEditPersonalDetails: () => void;
  onEditPayment: () => void;
  onChipNumberChange: (value: string | null) => void;
  customerGender?: "male" | "female" | null;
  contactBirthdate?: Date | undefined;
  selectedDogSize?: "small" | "large" | null;
}

const ConfirmationPage: React.FC<ConfirmationPageProps> = ({
  finalPrice,
  currentOffer,
  contactFirstName,
  contactLastName,
  contactEmail,
  contactPhone,
  addressLine1,
  addressLine2,
  city,
  zipCode,
  petType,
  petName,
  breed,
  breedLabel,
  birthDate,
  selectedBreedType,
  gender,
  isCastrated,
  isHouseCat,
  chipNumber,
  preexistingConditionCategories,
  preexistingConditionComment,
  coverageSelections,
  declinedCoverages,
  subCoverageSelections,
  subCoverageSumInsured,
  coverageDeductibles,
  coverageSumInsured,
  coverageSumInsuredPerCase,
  emergencyCareSumInsuredPerDay,
  annualDeductible,
  campaignCode,
  paymentInterval,
  paymentMethod,
  startOfInsurance,
  firstDue,
  dailyTermination,
  dispatchContract,
  dispatchInvoice,
  dispatchEmail,
  agentDispatchEmail,
  consultationProtocolDocumentId,
  consultationProtocolFilename,
  onStartNewQuote,
  onBack,
  onApplyOffer,
  isLoading,
  onEditPlanAndCoverage,
  onEditPetDetails,
  onEditPersonalDetails,
  onEditPayment,
  onChipNumberChange,
  customerGender = null,
  contactBirthdate,
  selectedDogSize = null,
}) => {
  const { t, i18n } = useTranslation();
  const { isCustomAgentId, agentToken } = useAgentId(); // Use the hook
  const petNameLabel = (petName || "").trim() || t("common.yourPet");
  const [documentsAccepted, setDocumentsAccepted] = React.useState(false);
  const [applyAttempted, setApplyAttempted] = React.useState(false);
  const [chipNumberError, setChipNumberError] = React.useState("");
  const showChipNumberInput = true;
  const chipNumberInputRef = React.useRef<HTMLInputElement>(null);
  const isAgentAuthenticated = !!agentToken;
  const [showEditPetDetailsConfirm, setShowEditPetDetailsConfirm] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const avbLink = t("common.avbLink") as string;
  const agbLink = t("common.agbLink") as string;
  const privacyLink = t("common.privacyPolicyLink") as string;

  const formatDate = (d: Date) => {
    try {
      return d.toLocaleDateString(i18n.language);
    } catch {
      return d.toISOString().split("T")[0];
    }
  };

  const deductibleResetDate = React.useMemo(() => {
    if (!isValid(startOfInsurance)) return null;
    return firstDue && isValid(firstDue)
      ? clampDeductibleResetDate(firstDue, startOfInsurance)
      : getDefaultDeductibleResetDate(startOfInsurance);
  }, [firstDue, startOfInsurance]);

  const yesNo = (v: boolean) => (v ? t("common.yes") : t("common.no"));

  const intervalLabel = (interval: 1 | 2 | 4 | 12) => {
    return t(`priceCustomization.paymentIntervalOptions.${interval}`);
  };

  const methodLabel = (method: "credit_card" | "ESR") => {
    return t(`priceCustomization.paymentMethodOptions.${method}`);
  };

  const validateChipNumber = (value: string | null) => !value || /^\d{15}$/.test(value);
  const ensureChipNumberValid = () => {
    if (validateChipNumber(chipNumber)) {
      setChipNumberError("");
      return true;
    }
    setChipNumberError(t("petDetails.validation.chipNumber"));
    if (chipNumberInputRef.current) {
      chipNumberInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      chipNumberInputRef.current.focus();
    }
    return false;
  };

  const SectionHeader: React.FC<{ title: string; onEdit: () => void; aside?: React.ReactNode }> = ({ title, onEdit, aside }) => (
    <div className="flex items-center justify-between gap-4">
      <h3 className="text-xl font-semibold">{title}</h3>
      <div className="flex items-center gap-2">
        {aside}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Edit"
          onClick={onEdit}
          className="text-primary hover:text-primary/80"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );


  const displayBreed = React.useMemo(() => {
    if (selectedBreedType === "mixed") {
      return breedLabel || t(`petDetails.breedType${selectedBreedType?.charAt(0).toUpperCase() + selectedBreedType?.slice(1)}`);
    }
    return breedLabel || (breed ? breed.replace(/_/g, " ") : "");
  }, [selectedBreedType, breedLabel, breed, t]);

  const documentAcceptanceTextKey = isCustomAgentId
    ? "confirmation.summary.agentAcceptPrefix" 
    : "confirmation.summary.acceptPrefix"; 

  const documentAcceptanceTextKeyAnd = isCustomAgentId
    ? "confirmation.summary.agentAnd"
    : "confirmation.summary.and";

  const documentAcceptanceTextKeyAnd2 = isCustomAgentId
    ? "confirmation.summary.agentAnd2"
    : "confirmation.summary.and2";

  const documentAcceptanceTextKeyPostfix = isCustomAgentId
    ? "confirmation.summary.agentAcceptPostfix" 
    : "confirmation.summary.acceptPostfix"; 

  const dispatchOptionLabel = (value: "customer" | "customer_and_agent" | "agent") => {
    if (value === "customer") return t("broker-display-customer");
    if (value === "customer_and_agent") return t("broker-display-customer-and-agent");
    return t("broker-display-agent");
  };


  return (
    <>
    <Card className="w-full max-w-4xl mx-auto border-none shadow-none">
      <CardHeader className="pb-0">
        <CardTitle className="text-3xl font-bold">{t("banner-header-4", { petName: petNameLabel })}</CardTitle>
        <p className="text-muted-foreground">{t("confirmation.summary.subtitle", { petName: petNameLabel })}</p>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="pt-6 border-t">
          <SectionHeader
            title={t("confirmation.summary.coverageHeader", { petName: petNameLabel })}
            onEdit={onEditPlanAndCoverage}
            aside={
              <span className="whitespace-nowrap text-lg font-bold text-destructive">
                CHF {finalPrice.toFixed(2)} / {t(`common.paymentIntervalOptions.${paymentInterval}`)}
              </span>
            }
          />
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <OfferCoverageList
              offer={currentOffer}
              coverageSelections={coverageSelections}
              declinedCoverages={declinedCoverages}
              petBirthDate={birthDate}
              valueAlign="left"
            />
          </div>
        </div>

        <div className="pt-6">
          <SectionHeader title={t("closing-pet-information-header", { petName: petNameLabel })} onEdit={() => setShowEditPetDetailsConfirm(true)} />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">{t("confirmation.summary.petTypeLabel")}:</span> <span>{t(`petSelection.${petType}`)}</span></div>
            <div><span className="font-medium">{t("confirmation.summary.petNameLabel")}:</span> <span>{petName}</span></div>
            <div><span className="font-medium">{t("confirmation.summary.petBreedLabel")}:</span> <span>{displayBreed}</span></div>
            {petType === "dog" && selectedBreedType === "mixed" && selectedDogSize !== null && (
              <div><span className="font-medium">{t("confirmation.summary.petTallDogLabel")}:</span> <span>{selectedDogSize === "large" ? t("common.yes") : t("common.no")}</span></div>
            )}
            <div><span className="font-medium">{t("confirmation.summary.petBirthdateLabel")}:</span> <span>{formatDate(birthDate)}</span></div>
            <div><span className="font-medium">{t("confirmation.summary.petGenderLabel")}:</span> <span>{t(`petDetails.gender${gender?.charAt(0).toUpperCase() + gender?.slice(1)}`)}</span></div>
            <div><span className="font-medium">{t("confirmation.summary.petCastratedLabel")}:</span> <span>{yesNo(isCastrated)}</span></div>
            {petType === "cat" && (
              <div><span className="font-medium">{t("confirmation.summary.petHouseCatLabel")}:</span> <span>{yesNo(isHouseCat)}</span></div>
            )}
            {chipNumber && !showChipNumberInput && (
              <div><span className="font-medium">{t("petDetails.chipNumberLabelOptional")}:</span> <span>{chipNumber}</span></div>
            )}
          </div>
          {preexistingConditionCategories.length > 0 && (
            <div className="mt-4 text-sm">
              <span className="font-medium">{t("confirmation.summary.preexistingConditionCategoriesLabel")}:</span>{" "}
              {preexistingConditionCategories
                .map((id) => {
                  const label = t(PREEXISTING_CONDITION_CATEGORIES.find((c) => c.id === id)?.labelKey ?? id);
                  return id === "other" && preexistingConditionComment
                    ? `${label} (${preexistingConditionComment})`
                    : label;
                })
                .join(", ")}
            </div>
          )}
          {showChipNumberInput && (
            <div className="mt-4">
              <ChipNumberInput
                chipNumber={chipNumber || ""}
                onChipNumberChange={(value) => {
                  onChipNumberChange(value.trim() ? value : null);
                  setChipNumberError("");
                }}
                error={chipNumberError}
                isLoading={isLoading}
                inputRef={chipNumberInputRef}
                showHint={false}
              />
            </div>
          )}
        </div>

        <div className="pt-6 border-t">
          <SectionHeader title={t("closing-begin-and-pay-header")} onEdit={onEditPayment} />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">{t("confirmation.summary.startDateLabel")}:</span> <span>{formatDate(startOfInsurance)}</span></div>
            <div><span className="font-medium">{t("confirmation.summary.dailyTerminationLabel")}:</span> <span>{dailyTermination ? t("common.yes") : t("common.no")}</span></div>
            {isCustomAgentId && deductibleResetDate && (
              <div><span className="font-medium">{t((annualDeductible ?? 0) > 0 ? "confirmation.summary.deductibleResetLabel" : "confirmation.summary.deductibleResetLabelNoDeductible")}:</span> <span>{format(deductibleResetDate, "d/M")}</span></div>
            )}
            <div><span className="font-medium">{t("confirmation.summary.paymentIntervalLabel")}:</span> <span>{intervalLabel(paymentInterval)}</span></div>
            <div><span className="font-medium">{t("confirmation.summary.paymentMethodLabel")}:</span> <span>{methodLabel(paymentMethod)}</span></div>
          </div>
        </div>

        <div className="pt-6 border-t">
          <SectionHeader title={t(isCustomAgentId ? "contactDetails.customerDataTitle" : "confirmation.summary.aboutYouHeader")} onEdit={onEditPersonalDetails} />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div>
              {customerGender
                ? t(`confirmation.summary.salutation.${customerGender}` as const)
                : t("confirmation.summary.yourSalutationPlaceholder")}
            </div>
            <div>{`${contactFirstName} ${contactLastName}`}</div>
            <div>{contactEmail}</div>
            {contactPhone && <div>{contactPhone}</div>}
            <div>
              <span className="font-medium">{t("confirmation.summary.yourBirthdateLabel")}:</span>{" "}
              {contactBirthdate ? formatDate(contactBirthdate) : t("common.notApplicable")}
            </div>
            {campaignCode?.trim() && (
              <div><span className="font-medium">{t("confirmation.summary.campaignCodeLabel")}:</span> <span>{campaignCode.trim()}</span></div>
            )}
          </div>
          <div className="mt-4">
            <h4 className="text-base font-semibold">{t(isCustomAgentId ? "confirmation.summary.customerAddressHeader" : "confirmation.summary.yourAddressHeader")}</h4>
            <div className="mt-2 text-sm space-y-1">
              <div><span>{addressLine1}</span> <span>{addressLine2}</span></div>
              <div><span>{zipCode}</span> <span>{city}</span></div>
            </div>
          </div>
        </div>

        {isAgentAuthenticated && (
          <div className="pt-6 border-t">
            <SectionHeader title={t("contactDetails.dispatchControlTitle")} onEdit={onEditPersonalDetails} />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {agentDispatchEmail && (
                <div><span className="font-medium">{t("contactDetails.agentEmailLabel")}:</span> <span>{agentDispatchEmail}</span></div>
              )}
              <div><span className="font-medium">{t("contactDetails.dispatchContracts")}:</span> <span>{dispatchOptionLabel(dispatchContract)}</span></div>
              <div><span className="font-medium">{t("contactDetails.dispatchInvoices")}:</span> <span>{dispatchOptionLabel(dispatchInvoice)}</span></div>
              <div><span className="font-medium">{t("contactDetails.dispatchEmails")}:</span> <span>{dispatchOptionLabel(dispatchEmail)}</span></div>
            </div>
          </div>
        )}

        {isAgentAuthenticated && (
          <div className="pt-6 border-t">
            <SectionHeader title={t("consultationProtocol.input")} onEdit={onEditPersonalDetails} />
            <p className="mt-4 text-sm">
              {consultationProtocolDocumentId
                ? consultationProtocolFilename || t("consultationProtocol.fallbackFilename")
                : t("consultationProtocol.noneAttached")}
            </p>
          </div>
        )}

        <div className="pt-6 border-t">
          <div className="flex items-start gap-3">
            <Checkbox
              id="docs-accepted"
              checked={documentsAccepted}
              onCheckedChange={(v) => setDocumentsAccepted(!!v)}
              className="mt-1"
            />
            <Label htmlFor="docs-accepted" className="text-sm leading-relaxed">
              {t(documentAcceptanceTextKey)}{" "}
              <a
                href={avbLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4 hover:opacity-80"
              >
                {t("common.avb")}
              </a>
              {t(documentAcceptanceTextKeyAnd)}
              <a
                href={agbLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4 hover:opacity-80"
              >
                {t("common.agb")}
              </a>
              {t(documentAcceptanceTextKeyAnd2)}
              <a
                  href={privacyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4 hover:opacity-80"
              >
                {t("common.privacyPolicy")}
              </a>
              {t(documentAcceptanceTextKeyPostfix)}
            </Label>
          </div>
          {!documentsAccepted && applyAttempted && (
            <p className="text-sm text-destructive mt-2">{t("confirmation.pleaseConfirm")}</p>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex flex-col gap-3">
        <div className="flex w-full flex-col-reverse gap-3">
          <Button variant="outline" onClick={onBack} className="w-full h-12 text-lg font-bold" disabled={isLoading}>
            {t("contactDetails.back")}
          </Button>
          <Button
            onClick={() => {
              if (isLoading) return;
              if (!documentsAccepted) {
                setApplyAttempted(true);
                return;
              }
              if (!ensureChipNumberValid()) return;
              onApplyOffer(contactFirstName || "", contactLastName || "");
            }}
            className="w-full h-12 text-lg font-bold bg-continue-button text-continue-button-foreground hover:bg-continue-button/90"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("confirmation.applyOffer")}
          </Button>
        </div>
      </CardFooter>
    </Card>
    <AlertDialog open={showEditPetDetailsConfirm} onOpenChange={setShowEditPetDetailsConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="sr-only">
            {t("confirmation.editPetDetailsConfirm.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("confirmation.editPetDetailsConfirm.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("confirmation.editPetDetailsConfirm.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={onEditPetDetails}>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default ConfirmationPage;
